import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { coursesAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  GraduationCap,
  Globe,
  Languages,
  Briefcase,
  ArrowRight,
  Sparkles,
  Loader2,
  MapPin,
  Building,
  DollarSign
} from 'lucide-react';

const CheckEligibilityPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [eligibilityScore, setEligibilityScore] = useState(0);
  const [matchingCourses, setMatchingCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  
  const [formData, setFormData] = useState({
    education_level: '',
    gpa: '',
    english_test: '',
    english_score: '',
    work_experience: '',
    target_country: '',
    target_degree: '',
    field_of_study: ''
  });

  const calculateEligibility = async () => {
    let score = 0;
    
    // Education level scoring
    if (formData.education_level === 'bachelors') score += 25;
    else if (formData.education_level === 'masters') score += 30;
    else if (formData.education_level === 'high_school') score += 15;
    
    // GPA scoring
    const gpa = parseFloat(formData.gpa);
    if (gpa >= 3.5) score += 25;
    else if (gpa >= 3.0) score += 20;
    else if (gpa >= 2.5) score += 15;
    else score += 10;
    
    // English score
    const engScore = parseFloat(formData.english_score);
    if (formData.english_test === 'IELTS') {
      if (engScore >= 7.0) score += 25;
      else if (engScore >= 6.5) score += 20;
      else if (engScore >= 6.0) score += 15;
      else score += 10;
    } else if (formData.english_test === 'PEARSON_PTE') {
      if (engScore >= 65) score += 25;
      else if (engScore >= 58) score += 20;
      else if (engScore >= 50) score += 15;
      else score += 10;
    } else if (formData.english_test === 'NONE') {
      score += 5;
    } else {
      score += 15;
    }
    
    // Work experience
    const exp = parseInt(formData.work_experience);
    if (exp >= 3) score += 15;
    else if (exp >= 1) score += 10;
    else score += 5;
    
    setEligibilityScore(Math.min(score, 100));
    
    // Fetch matching courses from backend
    setLoadingCourses(true);
    try {
      const params = {
        country: formData.target_country,
        degree_level: formData.target_degree
      };
      if (formData.field_of_study) {
        params.field = formData.field_of_study;
      }
      
      const response = await coursesAPI.getAll(params);
      const courses = response.data || [];
      // Show top 6 matching courses
      setMatchingCourses(courses.slice(0, 6));
    } catch (error) {
      console.error('Error fetching courses:', error);
      setMatchingCourses([]);
    } finally {
      setLoadingCourses(false);
    }
    
    setShowResults(true);
  };

  const getEligibilityStatus = () => {
    if (eligibilityScore >= 80) return { status: 'Excellent', color: 'text-green-600', bg: 'bg-green-100' };
    if (eligibilityScore >= 60) return { status: 'Good', color: 'text-teal-600', bg: 'bg-teal-100' };
    if (eligibilityScore >= 40) return { status: 'Fair', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { status: 'Needs Improvement', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const educationLevels = [
    { value: 'high_school', label: 'High School / A-Levels' },
    { value: 'bachelors', label: "Bachelor's Degree" },
    { value: 'masters', label: "Master's Degree" },
    { value: 'phd', label: 'PhD / Doctorate' }
  ];

  const englishTests = [
    { value: 'IELTS', label: 'IELTS' },
    { value: 'PEARSON_PTE', label: 'Pearson PTE' },
    { value: 'TOEFL', label: 'TOEFL' },
    { value: 'OXFORD', label: 'Oxford Test' },
    { value: 'DUOLINGO', label: 'Duolingo English Test' },
    { value: 'NONE', label: 'Not taken yet' }
  ];

  const countries = ['UK', 'USA', 'Canada', 'Ireland', 'France', 'Germany'];
  
  const fieldsOfStudy = [
    'Business & Management',
    'Computer Science & IT',
    'Engineering',
    'Healthcare & Medicine',
    'Arts & Design',
    'Law',
    'Social Sciences',
    'Natural Sciences',
    'Education',
    'Other'
  ];

  const formatCurrency = (amount, currency, country) => {
    const countryCurrencyMap = {
      'UK': 'GBP', 'United Kingdom': 'GBP',
      'USA': 'USD', 'United States': 'USD',
      'Canada': 'CAD', 'Australia': 'AUD',
      'Germany': 'EUR', 'France': 'EUR', 'Netherlands': 'EUR', 'Ireland': 'EUR',
      'New Zealand': 'NZD'
    };
    const currencyCode = currency || countryCurrencyMap[country] || 'GBP';
    const locale = currencyCode === 'GBP' ? 'en-GB' : currencyCode === 'EUR' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Hero */}
        <div className="bg-gradient-to-r from-teal-100/80 dark:from-slate-800 via-[#e9ecef] dark:via-slate-800 to-[#f1f3f5] dark:to-slate-900 py-12 transition-colors">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600/10 dark:bg-teal-900/50 border border-teal-300 dark:border-teal-700 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-teal-600 dark:text-teal-400" />
              <span className="text-teal-600 dark:text-teal-400 text-sm font-medium">Free Eligibility Check</span>
            </div>
            <h1 className="text-4xl font-semibold text-gray-800 dark:text-white mb-4">Check Your Eligibility</h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Find out if you qualify for your dream university in just 2 minutes
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {!showResults ? (
            <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-8 transition-colors">
              {/* Progress */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
                  <span>Step {step} of 3</span>
                  <span>{Math.round((step / 3) * 100)}% Complete</span>
                </div>
                <Progress value={(step / 3) * 100} className="h-2" />
              </div>

              {step === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-teal-600/10 flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Education Background</h2>
                      <p className="text-gray-500 text-sm">Tell us about your qualifications</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">Highest Education Level</Label>
                    <Select value={formData.education_level} onValueChange={(v) => setFormData({...formData, education_level: v})}>
                      <SelectTrigger className="bg-white border-gray-200 h-12" data-testid="eligibility-education">
                        <SelectValue placeholder="Select education level" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f1f3f5] border-gray-200">
                        {educationLevels.map(level => (
                          <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">GPA / Percentage (out of 4.0 or equivalent)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="4"
                      placeholder="e.g., 3.5"
                      value={formData.gpa}
                      onChange={(e) => setFormData({...formData, gpa: e.target.value})}
                      className="bg-white border-gray-200 h-12"
                      data-testid="eligibility-gpa"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">Preferred Field of Study</Label>
                    <Select value={formData.field_of_study} onValueChange={(v) => setFormData({...formData, field_of_study: v})}>
                      <SelectTrigger className="bg-white border-gray-200 h-12" data-testid="eligibility-field">
                        <SelectValue placeholder="Select field (optional)" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f1f3f5] border-gray-200">
                        {fieldsOfStudy.map(field => (
                          <SelectItem key={field} value={field}>{field}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={() => setStep(2)}
                    disabled={!formData.education_level || !formData.gpa}
                    className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold h-12"
                  >
                    Next Step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-teal-600/10 flex items-center justify-center">
                      <Languages className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-800 dark:text-white">English Proficiency</h2>
                      <p className="text-gray-500 text-sm">Your language test results</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">English Test Type</Label>
                    <Select value={formData.english_test} onValueChange={(v) => setFormData({...formData, english_test: v})}>
                      <SelectTrigger className="bg-white border-gray-200 h-12" data-testid="eligibility-english-test">
                        <SelectValue placeholder="Select test type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f1f3f5] border-gray-200">
                        {englishTests.map(test => (
                          <SelectItem key={test.value} value={test.value}>{test.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.english_test && formData.english_test !== 'NONE' && (
                    <div className="space-y-2">
                      <Label className="text-gray-600 dark:text-gray-300">Your Score</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder={formData.english_test === 'IELTS' ? 'e.g., 7.0' : 'e.g., 65'}
                        value={formData.english_score}
                        onChange={(e) => setFormData({...formData, english_score: e.target.value})}
                        className="bg-white border-gray-200 h-12"
                        data-testid="eligibility-english-score"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">Work Experience (Years)</Label>
                    <Select value={formData.work_experience} onValueChange={(v) => setFormData({...formData, work_experience: v})}>
                      <SelectTrigger className="bg-white border-gray-200 h-12" data-testid="eligibility-experience">
                        <SelectValue placeholder="Select experience" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f1f3f5] border-gray-200">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(year => (
                          <SelectItem key={year} value={year.toString()}>
                            {year === 0 ? 'No experience' : year === 10 ? '10+ years' : `${year} years`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-100 h-12"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      disabled={!formData.english_test}
                      className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-semibold h-12"
                    >
                      Next Step
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-teal-600/10 flex items-center justify-center">
                      <Globe className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Study Goals</h2>
                      <p className="text-gray-500 text-sm">Where do you want to study?</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">Target Country</Label>
                    <Select value={formData.target_country} onValueChange={(v) => setFormData({...formData, target_country: v})}>
                      <SelectTrigger className="bg-white border-gray-200 h-12" data-testid="eligibility-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f1f3f5] border-gray-200">
                        {countries.map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">Target Degree Level</Label>
                    <Select value={formData.target_degree} onValueChange={(v) => setFormData({...formData, target_degree: v})}>
                      <SelectTrigger className="bg-white border-gray-200 h-12" data-testid="eligibility-degree">
                        <SelectValue placeholder="Select degree" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f1f3f5] border-gray-200">
                        <SelectItem value="bachelors">Bachelor&apos;s Degree</SelectItem>
                        <SelectItem value="masters">Master&apos;s Degree</SelectItem>
                        <SelectItem value="phd">PhD / Doctorate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-100 h-12"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={calculateEligibility}
                      disabled={!formData.target_country || !formData.target_degree}
                      className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-semibold h-12"
                      data-testid="check-eligibility-btn"
                    >
                      Check Eligibility
                      <Sparkles className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Eligibility Score Card */}
              <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-8 text-center">
                <div className={`w-24 h-24 rounded-full ${getEligibilityStatus().bg} flex items-center justify-center mx-auto mb-6`}>
                  <span className={`text-4xl font-semibold ${getEligibilityStatus().color}`}>{eligibilityScore}%</span>
                </div>
                
                <Badge className={`${getEligibilityStatus().bg} ${getEligibilityStatus().color} text-lg px-4 py-1 mb-4`}>
                  {getEligibilityStatus().status}
                </Badge>
                
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">Your Eligibility Score</h2>
                
                <Progress value={eligibilityScore} className="h-3 mb-6" />
                
                <div className="grid grid-cols-2 gap-4 mb-8 text-left">
                  <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                    <p className="text-gray-500 text-sm">Target Country</p>
                    <p className="text-gray-800 font-medium">{formData.target_country}</p>
                  </div>
                  <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                    <p className="text-gray-500 text-sm">Target Degree</p>
                    <p className="text-gray-800 font-medium capitalize">{formData.target_degree}</p>
                  </div>
                </div>

                {eligibilityScore >= 60 ? (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg mb-6">
                    <div className="flex items-center gap-2 justify-center mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-green-600 font-medium">Good News!</span>
                    </div>
                    <p className="text-gray-600 text-sm">
                      You have a strong profile for studying in {formData.target_country}. Our experts can help you find the best universities.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-teal-500/10 border border-yellow-500/30 rounded-lg mb-6">
                    <div className="flex items-center gap-2 justify-center mb-2">
                      <AlertCircle className="w-5 h-5 text-teal-600" />
                      <span className="text-teal-600 font-medium">Recommendations</span>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Consider improving your English score or exploring universities with flexible entry requirements. Our experts can guide you.
                    </p>
                  </div>
                )}

                <div className="flex gap-4">
                  <Button
                    onClick={() => { setShowResults(false); setStep(1); setMatchingCourses([]); }}
                    variant="outline"
                    className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-100"
                  >
                    Check Again
                  </Button>
                  <Button
                    onClick={() => navigate(isAuthenticated ? '/courses' : '/register')}
                    className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                    data-testid="explore-courses-btn"
                  >
                    {isAuthenticated ? 'Explore Courses' : 'Get Started'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              {/* Matching Courses Section */}
              <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-teal-600" />
                  Matching Courses in {formData.target_country}
                </h3>
                
                {loadingCourses ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
                    <span className="text-gray-500 ml-3">Finding matching courses...</span>
                  </div>
                ) : matchingCourses.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {matchingCourses.map((course) => (
                      <div 
                        key={course.id} 
                        className="p-4 bg-[#e9ecef]/50 rounded-lg border border-gray-200 hover:border-teal-300 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <Badge className="bg-teal-600/20 text-teal-600 text-xs">
                            {course.degree_level}
                          </Badge>
                          <span className="text-teal-600 font-semibold text-sm">
                            {formatCurrency(course.tuition_fee, course.currency, course.country)}/yr
                          </span>
                        </div>
                        <h4 className="text-gray-800 font-medium mb-1 line-clamp-2">{course.name}</h4>
                        <div className="flex items-center gap-1 text-gray-500 text-sm mb-2">
                          <Building className="w-3 h-3" />
                          <span className="line-clamp-1">{course.university_name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {course.country}
                          </span>
                          <span>{course.duration}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-full bg-[#e9ecef] flex items-center justify-center mx-auto mb-3">
                      <GraduationCap className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">No courses found matching your criteria</p>
                    <p className="text-gray-500 text-sm">Try adjusting your preferences or browse all courses</p>
                  </div>
                )}

                {matchingCourses.length > 0 && (
                  <div className="mt-4 text-center">
                    <Button
                      onClick={() => navigate(`/courses?country=${formData.target_country}&degree_level=${formData.target_degree}`)}
                      variant="outline"
                      className="border-teal-400 text-teal-600 hover:bg-teal-600/10"
                    >
                      View All Matching Courses
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckEligibilityPage;
